import * as React from 'react';

import { fetch } from 'domain-task';

import Button, { ButtonProps } from '@common/react/components/Forms/Button';
import UseRequest from '@common/react/hooks/useRequest';
import { BaseParams } from '@common/react/objects/BaseParams';
import LoadingWrapper, { LoadingWrapperProps } from '@common/react/components/UI/LoadingWrapper/LoadingWrapper';

export type { SuccessProps } from '@common/react/components/UI/LoadingWrapper/LoadingWrapper';

export enum FetchMethod {
	POST,
	GET
}

export interface LoadingButtonProps<TResponse, ModalProps extends BaseParams = BaseParams> extends
	Omit<LoadingWrapperProps<TResponse, ModalProps>, 'children'>, ButtonProps {
	children: React.ReactNode;
	/*
	 * if we don't have it or it return true actionFunction will be called
	 */
	beforeAction?: (action, event: React.MouseEvent<HTMLButtonElement>) => boolean;
}

export interface RequestButtonProps<TResponse, TRequest = BaseParams, ModalProps extends BaseParams = BaseParams>
	extends Omit<LoadingButtonProps<TResponse, ModalProps>, 'actionFunction'> {
	requestType: string;
	requestProps?: TRequest;
}

export interface FetchButtonProps<ModalProps extends BaseParams = BaseParams> extends Omit<LoadingButtonProps<any, ModalProps>, 'actionFunction'> {
	path: string;
	data?: any;
	method?: FetchMethod;
}

interface FetchError {
	error: string;
}

interface FetchResponse {
	success: number;
	response: string;
}

const LoadingButton: <TResponse>(
	props: LoadingButtonProps<TResponse>,
) => React.ReactElement = <TResponse, >(
	props: LoadingButtonProps<TResponse>,
) => {
	const {
		actionFunction,
		successMessage,
		errorMessage,
		hideActionErrorMessage,
		useMessages,
		children,
		confirmationProps,
		beforeAction,
		...rest
	} = props;
	return <LoadingWrapper<TResponse>
		{...props}
	>
		{({ onClick, isLoading }) => <Button
			isLoading={isLoading}
			type="button"
			{...rest}
			onClick={onClick}
		>
			{children}
		</Button>}
	</LoadingWrapper>;
};

export const RequestButton: <TResponse, TRequest = BaseParams, ModalProps extends BaseParams = BaseParams>(
	props: RequestButtonProps<TResponse, TRequest, ModalProps>
) => React.ReactElement = <TResponse, TRequest, ModalProps extends BaseParams = BaseParams>(
	props: RequestButtonProps<TResponse, TRequest, ModalProps>,
) => {
	const {
		requestType,
		requestProps = {} as TRequest,
		children,
		...rest
	} = props;
	const request = UseRequest();

	return <>
		<LoadingButton
			{...rest}
			actionFunction={() => request<TResponse, TRequest>(requestType, requestProps)}
		>
			{children}
		</LoadingButton>
	</>;
};

export const isFetchError = (error): error is FetchError => {
	return (error as FetchError).error !== undefined
		&& `${(error as FetchError).error}`.toLowerCase() !== 'load failed'; // try to prevent showing error on IPad when file loaded
};

const isFetchResponse = (message): message is FetchResponse => {
	return (message as FetchResponse).success !== undefined
		&& (message as FetchResponse).response !== undefined;
};

export const handleFetchResponse = (response, defaultErrorMessage: string = '') => {
	const contentType = response.headers.get('content-type');
	const contentDisposition = response.headers.get('content-disposition');

	if (contentType && contentType.includes('application/json') && !contentDisposition?.includes('attachment')) {
		return response.json().then((res) => {
			if (response.status === 200) {
				if (isFetchResponse(res)) {
					if (res.success === 1) {
						return res.response;
					}

					throw res.response;
				}

				return res;
			}

			if (isFetchError(res)) {
				throw res.error;
			}

			throw defaultErrorMessage || response.statusText;
		});
	}

	if (response.status === 200) {
		return response;
	}

	throw defaultErrorMessage || response.statusText;
};

export const FetchButton: <ModalProps extends BaseParams = BaseParams>(
	props: FetchButtonProps<ModalProps>
) => React.ReactElement = <ModalProps extends BaseParams = BaseParams>(
	props: FetchButtonProps<ModalProps>,
) => {
	const {
		path,
		data = {},
		method,
		children,
		errorMessage,
		...rest
	} = props;

	const actionFunction = (): Promise<any> => {
		return fetch(path, {
			credentials: 'same-origin',
			method: method === FetchMethod.POST ? 'POST' : 'GET',
			headers: {
				'Content-type': 'application/json; charset=utf-8',
			},
			body: method === FetchMethod.POST ? JSON.stringify(data) : null,
		}).then((response) => {
			return handleFetchResponse(response, errorMessage);
		});
	};

	return <>
		<LoadingButton
			{...rest}
			actionFunction={actionFunction}
		>
			{children}
		</LoadingButton>
	</>;
};

export default LoadingButton;
